import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { formatCurrency } from '../../utils/formatCurrency';

function CustomerDashboard() {
  const navigate = useNavigate();
  const { customer, logout, getAuthHeader, isAuthenticated, loading: authLoading } = usePortalAuth();

  const [services, setServices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/portal/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [servicesRes, invoicesRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/portal/services`, { headers: getAuthHeader() }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/portal/invoices`, { headers: getAuthHeader() }),
        ]);
        setServices(servicesRes.data.slice(0, 5)); // Last 5 services
        setInvoices(invoicesRes.data);
      } catch (err) {
        console.error('Failed to load portal data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, getAuthHeader]);

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const unpaidInvoices = invoices.filter(i => i.paymentStatus !== 'paid' && !i.isComp);
  const unpaidTotal = unpaidInvoices.reduce((sum, i) => sum + i.total, 0);

  if (authLoading || (!isAuthenticated && !loading)) {
    return (
      <div className="portal-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <header className="portal-header">
        <div className="portal-header-content">
          <div className="portal-brand">
            <h1>{customer?.company?.name || 'Customer Portal'}</h1>
          </div>
          <nav className="portal-nav">
            <Link to="/portal" className="portal-nav-link active">Dashboard</Link>
            <Link to="/portal/services" className="portal-nav-link">Services</Link>
            <Link to="/portal/invoices" className="portal-nav-link">Invoices</Link>
            <button onClick={handleLogout} className="portal-nav-link logout">Logout</button>
          </nav>
        </div>
      </header>

      <main className="portal-main">
        <div className="portal-container">
          <div className="portal-welcome">
            <h2>Welcome back, {customer?.firstName}!</h2>
            <p>Here's an overview of your account.</p>
          </div>

          {/* Stats Cards */}
          <div className="portal-stats">
            <div className="stat-card">
              <div className="stat-value">{services.length > 0 ? services.length : '-'}</div>
              <div className="stat-label">Recent Services</div>
            </div>
            <div className={`stat-card ${unpaidTotal > 0 ? 'warning' : ''}`}>
              <div className="stat-value">${formatCurrency(unpaidTotal)}</div>
              <div className="stat-label">Outstanding Balance</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{invoices.filter(i => i.paymentStatus === 'paid').length}</div>
              <div className="stat-label">Paid Invoices</div>
            </div>
          </div>

          {/* Unpaid Invoices Alert */}
          {unpaidInvoices.length > 0 && (
            <div className="portal-alert">
              <div className="alert-content">
                <span className="alert-icon">!</span>
                <div className="alert-text">
                  <strong>You have {unpaidInvoices.length} unpaid invoice{unpaidInvoices.length > 1 ? 's' : ''}</strong>
                  <span> totaling ${formatCurrency(unpaidTotal)}</span>
                </div>
              </div>
              <Link to="/portal/invoices" className="alert-action">View Invoices</Link>
            </div>
          )}

          {/* Recent Services */}
          <div className="portal-section">
            <div className="section-header">
              <h3>Recent Services</h3>
              <Link to="/portal/services" className="section-link">View All</Link>
            </div>
            {loading ? (
              <div className="portal-loading-inline">Loading...</div>
            ) : services.length === 0 ? (
              <div className="portal-empty">No services yet.</div>
            ) : (
              <div className="services-list">
                {services.map(service => (
                  <div key={service.id} className="service-item">
                    <div className="service-date">{formatDate(service.completedAt)}</div>
                    <div className="service-details">
                      <span className="service-route">{service.routeName || 'Service'}</span>
                      {service.photoCount > 0 && (
                        <span className="photo-badge">📷 {service.photoCount}</span>
                      )}
                    </div>
                    <div className="service-price">
                      {service.isComp ? (
                        <span className="comp-tag">COMP</span>
                      ) : (
                        `$${formatCurrency(service.price)}`
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact Info */}
          {customer?.company && (
            <div className="portal-section contact-section">
              <h3>Contact Us</h3>
              <div className="contact-info">
                {customer.company.phone && (
                  <a href={`tel:${customer.company.phone}`} className="contact-item">
                    <span className="contact-icon">📞</span>
                    <span>{customer.company.phone}</span>
                  </a>
                )}
                {customer.company.email && (
                  <a href={`mailto:${customer.company.email}`} className="contact-item">
                    <span className="contact-icon">✉️</span>
                    <span>{customer.company.email}</span>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <style>{`
        .portal-page {
          min-height: 100vh;
          background: #f1f5f9;
        }
        .portal-loading {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
        }
        .portal-loading .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top-color: #16a34a;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .portal-header {
          background: linear-gradient(135deg, #14532d 0%, #1a3a2a 100%);
          color: white;
          padding: 16px 20px;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .portal-header-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .portal-brand h1 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }
        .portal-nav {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .portal-nav-link {
          background: rgba(255,255,255,0.1);
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 16px;
          border-radius: 6px;
          text-decoration: none;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.15s;
        }
        .portal-nav-link:hover, .portal-nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .portal-nav-link.logout {
          background: rgba(239,68,68,0.2);
          color: #fca5a5;
        }
        .portal-nav-link.logout:hover {
          background: rgba(239,68,68,0.3);
          color: white;
        }
        .portal-main {
          padding: 24px 20px;
        }
        .portal-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        .portal-welcome {
          margin-bottom: 24px;
        }
        .portal-welcome h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 4px 0;
        }
        .portal-welcome p {
          color: #64748b;
          margin: 0;
        }
        .portal-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }
        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .stat-card.warning {
          border-left: 4px solid #f59e0b;
        }
        .stat-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #0f172a;
        }
        .stat-label {
          font-size: 0.875rem;
          color: #64748b;
          margin-top: 4px;
        }
        .portal-alert {
          background: #fef3c7;
          border: 1px solid #fcd34d;
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        .alert-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .alert-icon {
          width: 28px;
          height: 28px;
          background: #f59e0b;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1rem;
        }
        .alert-text {
          color: #92400e;
          font-size: 0.95rem;
        }
        .alert-action {
          background: #f59e0b;
          color: white;
          padding: 8px 16px;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9rem;
        }
        .alert-action:hover {
          background: #d97706;
        }
        .portal-section {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          margin-bottom: 24px;
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .section-header h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0;
        }
        .section-link {
          color: #16a34a;
          text-decoration: none;
          font-size: 0.9rem;
        }
        .section-link:hover {
          text-decoration: underline;
        }
        .portal-loading-inline {
          padding: 20px;
          text-align: center;
          color: #64748b;
        }
        .portal-empty {
          padding: 20px;
          text-align: center;
          color: #64748b;
        }
        .services-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .service-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
        }
        .service-date {
          font-size: 0.875rem;
          color: #64748b;
          min-width: 100px;
        }
        .service-details {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .service-route {
          font-weight: 500;
          color: #0f172a;
        }
        .photo-badge {
          background: rgba(59,130,246,0.1);
          color: #16a34a;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 0.75rem;
        }
        .service-price {
          font-weight: 600;
          color: #0f172a;
        }
        .comp-tag {
          background: #dbeafe;
          color: #166534;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .contact-section h3 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 16px 0;
        }
        .contact-info {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }
        .contact-item {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #16a34a;
          text-decoration: none;
          font-size: 0.95rem;
        }
        .contact-item:hover {
          text-decoration: underline;
        }
        .contact-icon {
          font-size: 1.1rem;
        }

        @media (max-width: 640px) {
          .portal-header-content {
            flex-direction: column;
            align-items: flex-start;
          }
          .service-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
          .service-details {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>
    </div>
  );
}

export default CustomerDashboard;
